import { useCallback, useEffect, useMemo, useState } from 'react';
import { StepStatus, TIdentificationStep } from '../types';
import { useFeatureFlags } from '~/helpers/FeatureFlagsContext';
import { useSelector } from 'react-redux';
import {
  gettingStartedStep,
  generalInfoStep,
  relatedCompaniesStep,
  managementStructureStep,
  managersAndRepresentativesStep,
  ownershipStructureStep,
  additionalInformationStep,
  financialStateStep,
  purposeOfBusinessRelationsStep,
  documentsStep,
  taxResidencyAdditionalInformationStep,
  fatcaAdditionalInformationStep,
} from '../constants';
import { getCompleatedFormByStepName } from '../utils';
import useGetUserRole from '../../../hooks/useGetUserRole';
import { ClientTypes } from '~/constants/clientTypes';
import {
  selectApplicationId,
  selectClientType,
  selectCompletedForms,
} from '~/store/identification-form/selectors';
import { useGetIsStepDisabled } from './useGetIsStepDisabled';

export const useGetIdentificationSteps = (activeStepName: string) => {
  const {
    isCRSIFopEnabled,
    isAdditionalInformationAboutTaxResidencyEnabled,
    isFATCALegalResidentEnabled,
  } = useFeatureFlags();
  const { isAssetManager } = useGetUserRole();

  const completedForms = useSelector(selectCompletedForms);
  const applicationId = useSelector(selectApplicationId);
  const type = useSelector(selectClientType);
  const isStepDisabledByName = useGetIsStepDisabled();

  const isStepSkipped = useCallback(
    (index: number, name?: string) => isStepDisabledByName(name) || (isAssetManager && index === 0),
    [isStepDisabledByName, isAssetManager]
  );

  const legalResidentSteps = useMemo(
    () => [
      gettingStartedStep,
      generalInfoStep,
      relatedCompaniesStep,
      managementStructureStep,
      managersAndRepresentativesStep,
      ownershipStructureStep,
      additionalInformationStep,
      ...(isAdditionalInformationAboutTaxResidencyEnabled
        ? [taxResidencyAdditionalInformationStep]
        : []),
      ...(isFATCALegalResidentEnabled ? [fatcaAdditionalInformationStep] : []),
      financialStateStep,
      purposeOfBusinessRelationsStep,
      documentsStep,
    ],
    []
  );
  const soleTraderSteps = useMemo(
    () => [
      gettingStartedStep,
      generalInfoStep,
      isCRSIFopEnabled
        ? {
            ...additionalInformationStep,
            pageTitle: 'Додаткова інформація',
            sidebarTitle: 'Додаткова інформація',
          }
        : additionalInformationStep,
      financialStateStep,
      purposeOfBusinessRelationsStep,
      documentsStep,
    ],
    []
  );
  const assetManagerSteps = useMemo(() => [gettingStartedStep, generalInfoStep, documentsStep], []);
  const defaultSteps = useMemo(
    () =>
      [
        gettingStartedStep,
        generalInfoStep,
        financialStateStep,
        purposeOfBusinessRelationsStep,
        documentsStep,
      ].map((item, index) => (index ? item : { ...item, stepStatus: StepStatus.passive })),
    []
  );

  const [steps, setSteps] = useState<TIdentificationStep[]>(defaultSteps);

  const getStepsByType = (type: ClientTypes) => {
    switch (type) {
      case ClientTypes.ASSET_MANAGER:
        return assetManagerSteps;
      case ClientTypes.LEGAL_RESIDENT:
        return legalResidentSteps;
      case ClientTypes.SOLE_TRADER:
        return soleTraderSteps;
      default:
        return defaultSteps;
    }
  };

  useEffect(() => {
    if (!completedForms?.isGettingStartedCompleted || !applicationId || !type) return;

    const nextSteps = getStepsByType(type);

    const isCompleted = (name: string) =>
      !!completedForms[getCompleatedFormByStepName(name) as keyof typeof completedForms];

    setSteps(
      nextSteps.map((step, index) => {
        if (isStepSkipped(index, step.name)) return { ...step, stepStatus: StepStatus.disabled };

        let prevIndex = index - 1;
        while (prevIndex >= 0 && isStepSkipped(prevIndex, nextSteps[prevIndex]?.name)) {
          prevIndex -= 1;
        }

        const isUnlocked = prevIndex < 0 || isCompleted(nextSteps[prevIndex]?.name as string);

        if (isCompleted(step.name)) return { ...step, stepStatus: StepStatus.completed };
        return { ...step, stepStatus: isUnlocked ? StepStatus.passive : StepStatus.disabled };
      })
    );
  }, [completedForms, applicationId, type, activeStepName]);

  return {
    steps,
    setSteps,
    soleTraderSteps,
    legalResidentSteps,
    assetManagerSteps,
  };
};
